import React, { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Navigate, useLocation } from 'react-router-dom';
import { LoadingOverlayModal } from '../LoadingOverlayModal';
import { LoginRequiredModal } from './LoginRequiredModal';
import { sessionManager } from '@/services/sessionManager';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { user, status, hasAuthenticated } = useAuth();
  const location = useLocation();
  const [isReauthRequired, setIsReauthRequired] = useState(
    () => sessionManager.isReauthRequired()
  );

  useEffect(() => {
    const unsubscribeRequired = sessionManager.on('reauth-required', () => {
      setIsReauthRequired(true);
    });
    const clear = () => setIsReauthRequired(false);
    const unsubscribeSuccess = sessionManager.on('reauth-success', clear);
    const unsubscribeCancelled = sessionManager.on('reauth-cancelled', clear);

    return () => {
      unsubscribeRequired();
      unsubscribeSuccess();
      unsubscribeCancelled();
    };
  }, []);

  const nextPath = `${location.pathname}${location.search}${location.hash}`;

  // If user has never authenticated, avoid blocking on loading
  if (status === 'loading' && !hasAuthenticated) {
    const searchParams = new URLSearchParams();
    if (nextPath && nextPath !== '/login') {
      searchParams.set('next', nextPath);
    }
    const nextQuery = searchParams.toString();
    const loginPath = nextQuery ? `/login?${nextQuery}` : '/login';
    return <Navigate to={loginPath} replace />;
  }

  // Show loading while checking authentication
  if (status === 'loading') {
    return (
      <>
        <LoadingOverlayModal />
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100vh',
            fontSize: '1.2rem',
          }}
        >
          Loading...
        </div>
      </>
    );
  }

  // Show inline login if not authenticated
  if (!user || status === 'error') {
    if (hasAuthenticated) {
      // During reauthentication we should NOT show the embedded login screen
      // behind the session-expired modal. The modal is the single UX entry point.
      if (isReauthRequired) {
        // Keep rendering the protected shell to avoid a blank screen while the
        // session-expired modal (rendered globally) is shown.
        return <>{children}</>;
      }
      return (
        <>
          <LoadingOverlayModal />
          <LoginRequiredModal isOpen nextPath={nextPath} />
        </>
      );
    }
    const searchParams = new URLSearchParams();
    if (nextPath && nextPath !== '/login') {
      searchParams.set('next', nextPath);
    }
    const nextQuery = searchParams.toString();
    const loginPath = nextQuery ? `/login?${nextQuery}` : '/login';
    return <Navigate to={loginPath} replace />;
  }

  // User is authenticated, render children
  return <>{children}</>;
};

export default ProtectedRoute;
